// Canonical list of every package-format adapter the API ships.
//
// Single source of truth for:
//   * the create-repository dropdown (Repos.tsx)
//   * the Repo['format'] TypeScript union (api/client.ts) via `FormatId`
//   * the dashboard format-adapters grid (Home.tsx)
//
// When a new format ships in `api/internal/formats/*`, add a row here
// and the dropdown, the type union, and the dashboard count all stay
// in sync without having to chase three call sites.
//
// Field guide:
//   id       — the wire format string the API expects (`format` column on
//              the repos table). Stable; changing it is a DB migration.
//   label    — human-readable name shown in the UI dropdown and the
//              dashboard tile.
//   slug     — simple-icons identifier matching a vendored SVG under
//              `frontend/public/format-icons/<slug>.svg`. `null` means
//              there's no recognisable brand logo; the UI falls back to a
//              generic Box icon for those rows. The `prebuild` hook runs
//              `scripts/sync-format-icons.mjs --check` so a typo here
//              fails the build instead of silently 404-ing at runtime.
//   caveat   — optional, user-facing note on a known limitation (surfaced
//              in the create-repo dropdown and the dashboard tile tooltip)
//              so the UI never oversells an adapter that's thinner than
//              its peers. Mirrors the "minimal / partial / stub" list in
//              CLAUDE.md §5.1 and `docs-site/formats/`; keep them in sync
//              when either changes (roadmap item 148, Format-Support-Matrix).

export const FORMATS = [
  { id: 'raw',         label: 'Generic',        slug: null },
  { id: 'docker',      label: 'Docker / OCI',   slug: 'docker' },
  { id: 'maven',       label: 'Maven',          slug: 'apachemaven' },
  { id: 'npm',         label: 'npm',            slug: 'npm' },
  { id: 'pypi',        label: 'PyPI',           slug: 'pypi' },
  { id: 'helm',        label: 'Helm',           slug: 'helm' },
  { id: 'debian',      label: 'Debian (apt)',   slug: 'debian' },
  { id: 'rpm',         label: 'RPM (yum/dnf)',  slug: 'redhat' },
  { id: 'nuget',       label: 'NuGet',          slug: 'nuget',
    caveat: 'V3 search/autocomplete endpoints are stubbed; publish and restore (V2) are fully supported.' },
  { id: 'cargo',       label: 'Cargo',          slug: 'rust' },
  { id: 'go',          label: 'Go Modules',     slug: 'go' },
  { id: 'lfs',         label: 'Git LFS',        slug: 'gitlfs' },
  { id: 'terraform',   label: 'Terraform',      slug: 'terraform' },
  { id: 'rubygems',    label: 'RubyGems',       slug: 'rubygems' },
  { id: 'composer',    label: 'Composer',       slug: 'composer' },
  { id: 'conan',       label: 'Conan',          slug: 'conan' },
  { id: 'conda',       label: 'Conda',          slug: 'anaconda' },
  { id: 'pub',         label: 'Pub (Dart)',     slug: 'dart' },
  { id: 'supermarket', label: 'Chef',           slug: 'chef' },
  { id: 'alpine',      label: 'Alpine (apk)',   slug: 'alpinelinux' },
  { id: 'cran',        label: 'CRAN (R)',       slug: 'r', experimental: true,
    caveat: 'Archive browsing only (`archive.rds` stub); no live PACKAGES index generation yet.' },
  { id: 'vagrant',     label: 'Vagrant',        slug: 'vagrant' },
  { id: 'swift',       label: 'Swift',          slug: 'swift' },
  { id: 'nix',         label: 'Nix',            slug: 'nixos' },
  { id: 'opkg',        label: 'Opkg (OpenWrt)', slug: 'openwrt' },
  { id: 'bower',       label: 'Bower',          slug: 'bower' },
  { id: 'cocoapods',   label: 'CocoaPods',      slug: 'cocoapods' },
  { id: 'hex',         label: 'Hex',            slug: 'elixir' },
  { id: 'p2',          label: 'P2 (Eclipse)',   slug: 'eclipseide' },
  { id: 'puppet',      label: 'Puppet Forge',   slug: 'puppet', experimental: true,
    caveat: 'Some Forge metadata fields are stubbed.' },
  { id: 'bazel',       label: 'Bazel',          slug: 'bazel' },
  { id: 'jetbrains',   label: 'JetBrains',      slug: 'jetbrains' },
  // VSIX extensions are a Visual Studio package format; the simple-
  // icons `vscodium` slug only fits the VSCodium fork, not the format
  // itself, so fall through to the generic Box icon instead of
  // mis-branding.
  { id: 'vsix',        label: 'VSIX',           slug: null, experimental: true,
    caveat: 'Namespace/publisher creation is a stub; upload and download work.' },
  // NVIDIA NIM is the Inference-Microservice runtime; the generic
  // `nvidia` corporate logo doesn't read as NIM-specific, so fall
  // through to the generic Box icon rather than mis-brand the tile.
  { id: 'nim',         label: 'NVIDIA NIM',     slug: null },
  { id: 'huggingface', label: 'Hugging Face',   slug: 'huggingface' },
  { id: 'argoworkflow',label: 'Argo Workflows', slug: 'argo' },
  { id: 'deno',        label: 'Deno',           slug: 'deno' },
  { id: 'homebrew',    label: 'Homebrew Tap',   slug: 'homebrew', experimental: true,
    caveat: 'Read-only tap mirror; no publish support.' },
  { id: 'ollama',      label: 'Ollama',         slug: 'ollama' },
  { id: 'kustomize',   label: 'Kustomize',      slug: 'kubernetes' },
  // simple-icons doesn't ship an Open Policy Agent glyph as of v16, so
  // fall back to the generic Box icon rather than wire a 404. Switch
  // the slug back if upstream adds it.
  { id: 'opa',         label: 'OPA Bundles',    slug: null },
  // Registered backend adapter (`api/internal/formats/vcs`) that was
  // missing from this list entirely — repos of this format existed
  // only reachable via direct API/CLI use, never selectable here.
  { id: 'vcs',         label: 'Generic VCS',    slug: null, experimental: true,
    caveat: 'Read-through HEAD-probe proxy only (e.g. `go get`); no full clone/fetch proxying.' },
  // Roadmap item 149 — a governed registry for the skills/agents/prompts an
  // org's coding agents load ("npm for agents"). Phase A shipped the
  // format adapter, Phase B the dedicated Skill-Browser UI
  // (`pages/projects/AgentSkillPackages.tsx`); Phase C (MCP server) is
  // still open.
  { id: 'agentskill',  label: 'Agent/Skill Packages', slug: null, experimental: true,
    caveat: 'New in this release — publish/pull/promote work; no MCP server yet.' },
] as const satisfies readonly { id: string; label: string; slug: string | null; experimental?: boolean; caveat?: string }[];

// FormatId is the union of every supported `id` value, derived from
// FORMATS so the type union and the runtime list cannot drift apart.
export type FormatId = (typeof FORMATS)[number]['id'];

export type FormatDescriptor = (typeof FORMATS)[number];

// Individual FORMATS entries only carry `experimental`/`caveat` when set, so
// each element's inferred literal type differs — callers narrowing on those
// fields directly (`f.experimental`) hit a TS2339 for entries missing them.
// These accessors give one place that does the narrowing.
export function isExperimentalFormat(f: FormatDescriptor): boolean {
  return 'experimental' in f && f.experimental === true;
}

export function formatCaveat(f: FormatDescriptor): string | undefined {
  return 'caveat' in f ? f.caveat : undefined;
}
